export type Alumnus = {
  /** Stable key — names repeat across the list, so don't key on those. */
  id: string;
  /** Which programme the scores belong to; drives nothing visual today. */
  track: "CPA" | "CMA";
  name: string;
  /** Empty for a couple of records upstream, so the card omits the line. */
  designation: string;
  company: string;
  /**
   * Bare ImageKit URLs, with NO `?tr=` query of their own. CpaCmaAlumni appends
   * its own transform sized to how the image actually renders, so anything
   * hardcoded here would be dropped and the full-size original downloaded.
   *
   * `logo` is optional: two employers have no mark in the asset sheet yet, and
   * the card falls back to setting the company name in type for those.
   */
  logo?: string;
  /** Intrinsic logo dimensions — used for the aspect ratio, so it can't shift. */
  logoWidth?: number;
  logoHeight?: number;
  image: string;
  quote: string;
  /** Pre-formatted pills, e.g. "85 FAR" or "410 Part 1". */
  scores: string[];
};

const IK = "https://ik.imagekit.io/mileseducation/miles_website/home_2026";

/**
 * Sourced from the CPA and CMA testimonial sets — AboutCPA.tsx and AboutCMA.tsx
 * — with photos and company marks from the alumni asset sheet. The two tracks
 * are interleaved so the cards on screen first aren't all one programme.
 *
 * The carousel reads this array's length and nothing else, so this is the only
 * file to touch when the roster changes; it renders in batches, meaning the
 * count costs nothing on first paint.
 *
 * Missing upstream, and worth chasing:
 *   - Aditya Manoharan (CMA, EY GDS) is omitted entirely — no photo in the
 *     sheet. Add `alumni/Aditya%20Manoharan.png` and the record below returns.
 *   - EisnerAmper has no logo, so Vamshi Teja and Simran Saini fall back to
 *     text. Drop the file into company-logos/ and add the three logo fields.
 */
export const alumni: Alumnus[] = [
  {
    id: "cma-shanif-nizar",
    track: "CMA",
    name: "Shanif Nizar",
    designation: "Tax Associate",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Shanif%20Nizar.png`,
    quote:
      "I felt a lot more confident during placements because of how Miles prepared me overall. Along with their support i was able to get into KPMG.",
    scores: ["430 Part 1", "460 Part 2"],
  },
  {
    id: "cma-ishtaa-jain",
    track: "CMA",
    name: "Ishtaa Jain",
    designation: "Audit Associate",
    company: "EY",
    logo: `${IK}/company-logos/EY.svg`,
    logoWidth: 47,
    logoHeight: 48,
    image: `${IK}/alumni/Ishtaa%20Jain.png`,
    quote:
      "The clarity Varun Jain sir gave me is still my edge when solving audit problems at EY. That confidence, combined with the placement guidance at Miles, is how I ended up at EY.",
    scores: ["430 Part 1", "440 Part 2"],
  },
  {
    id: "cma-swapan-singla",
    track: "CMA",
    name: "Swapan Singla",
    designation: "Executive",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/Swapan%20Singla.png`,
    quote:
      "When placements started, the way their team guided me made all the difference in getting into Wipro. I walked into Wipro already thinking like an analyst, thanks to how practically Miles trained us during CMA.",
    scores: ["430 Part 1", "420 Part 2"],
  },
  {
    id: "cpa-sukanya",
    track: "CPA",
    name: "Sukanya",
    designation: "Audit Associate 2",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Sukanya.png`,
    quote:
      "Scoring 93 in REG felt achievable because of Miles mocks and Varun Jain's classes. Miles didn't just get me into KPMG, the way REG was taught made me faster at spotting issues than peers from other batches.",
    scores: ["85 FAR", "82 AUD", "93 REG", "88 BAR"],
  },
  {
    id: "cpa-prashant-gupta",
    track: "CPA",
    name: "Prashant Gupta",
    designation: "Senior Associate",
    company: "PWC",
    logo: `${IK}/company-logos/PWC.png`,
    logoWidth: 65,
    logoHeight: 48,
    image: `${IK}/alumni/Prashant%20Gupta.png`,
    quote:
      "Varun Jain sir's auditing classes gave me a clarity that still shows up in how quickly I get up to speed on new engagements at PWC.",
    scores: ["88 FAR", "81 AUD", "80 REG", "80 BAR"],
  },
  {
    id: "cma-tushar-saini",
    track: "CMA",
    name: "Tushar Saini",
    designation: "Management Trainee",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/Tushar%20Saini.png`,
    quote:
      "Miles made CMA concepts stick so well that I'm applying them daily at Wipro, not just recalling them for an exam and during placements, their support played a big part in helping me get into Wipro.",
    scores: ["400 Part 1", "440 Part 2"],
  },
  
  {
    id: "cpa-yashwanth-reddy-bobba",
    track: "CPA",
    name: "Yashwanth Reddy Bobba",
    designation: "Asst. Manager",
    company: "Deloitte",
    logo: `${IK}/company-logos/Delottie%20.png`,
    logoWidth: 256,
    logoHeight: 48,
    image: `${IK}/alumni/Yashwanth%20Reddy%20Bobba.png`,
    quote:
      "Miles' preparation strategy gave me the clarity to approach problems methodically which helped boost my growth to Assistant Manager at Deloitte today!",
    scores: ["88 FAR", "82 AUD", "86 REG", "82 BAR"],
  },
  {
    id: "cma-tanu-priya-sharma",
    track: "CMA",
    name: "Tanu Priya Sharma",
    designation: "Associate Consultant",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Tanu%20Priya%20Sharma.png`,
    quote:
      "Miles prepared me for more than clearing CMA, the fundamentals they built are what make me confident consulting clients at KPMG today.",
    scores: ["430 Part 1", "410 Part 2"],
  },
  {
    id: "cma-gaurang-asopa",
    track: "CMA",
    name: "Gaurang Asopa",
    designation: "Senior Analyst",
    company: "Goldman Sachs",
    logo: `${IK}/company-logos/Goldman%20Sachs.png`,
    logoWidth: 115,
    logoHeight: 48,
    image: `${IK}/alumni/Gaurang%20Asopa.png`,
    quote:
      "I still remember how Varun Jain sir explained a few tricky topics during my CMA prep at Miles, and that really stuck with me. That clarity, along with how the Miles team handled placements, is what eventually got me into Goldman Sachs.",
    scores: ["410 Part 1", "450 Part 2"],
  },
  {
    id: "cpa-shyam-santoshbhai-dandwani",
    track: "CPA",
    name: "Shyam Santoshbhai Dandwani",
    designation: "Sr Audit Analyst",
    company: "Wipfli LLP",
    logo: `${IK}/company-logos/Wiphli.png`,
    logoWidth: 148,
    logoHeight: 48,
    image: `${IK}/alumni/Shyam%20Santoshbhai%20Dandwani.png`,
    quote:
      "Varun Jain's classes made complex concepts simple, and Miles mocks helped me stay consistently exam-ready.  how I break down audit problems at Wipfli still traces back to how Varun Jain sir taught us to think.",
    scores: ["97 FAR", "89 AUD", "91 REG", "90 BAR"],
  },
  {
    id: "cpa-deep-ruparel",
    track: "CPA",
    name: "Deep Ruparel",
    designation: "Asst. Manager",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Deep%20Ruparel.png`,
    quote:
      "The consistency Varun Jain sir's sessions built in me is still visible in how I structure my work as Assistant Manager at KPMG.",
    scores: ["87 FAR", "79 AUD", "77 REG", "80 BAR"],
  },
  {
    id: "cma-aniketh-manjunatha",
    track: "CMA",
    name: "Aniketh Manjunatha",
    designation: "Executive",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/Aniketh%20Manjunatha.png`,
    quote:
      "460 score in one of the parts really gave me confidence and it's all thanks to Miles's placement support system that helped me get into Wipro",
    scores: ["430 Part 1", "460 Part 2"],
  },
  {
    id: "cpa-tejas-bhayani",
    track: "CPA",
    name: "Tejas Bhayani",
    designation: "Audit Associate",
    company: "Unison Globus",
    logo: `${IK}/company-logos/Unison%20Globus.png`,
    logoWidth: 74,
    logoHeight: 48,
    image: `${IK}/alumni/Tejas%20Bhayani.png`,
    quote:
      "Miles study materials and revision sessions made learning structured and helped improve across all subjects.",
    scores: ["86 FAR", "78 AUD", "82 REG", "83 BAR"],
  },
  {
    id: "cpa-jatin-satija",
    track: "CPA",
    name: "Jatin Satija",
    designation: "Senior Professional Finance",
    company: "DXC Technology",
    logo: `${IK}/company-logos/DXC.png`,
    logoWidth: 296,
    logoHeight: 48,
    image: `${IK}/alumni/Jatin%20Satija.png`,
    quote:
      "Breaking down BAR the way Miles taught gave me a problem-solving habit I still use in every review at DXC.",
    scores: ["97 FAR", "77 AUD", "87 REG", "82 BAR"],
  },
  {
    id: "cma-abinash-s",
    track: "CMA",
    name: "Abinash S",
    designation: "Tax Consultant",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Abinash%20S.png`,
    quote:
      "The routine Miles built during my CMA prep is the same discipline that keeps me organized. And at the time of placements, their involvement helped me secure  an opportunity at KPMG.",
    scores: ["430 Part 1", "430 Part 2"],
  },
  {
    id: "cma-anjali-chauhan",
    track: "CMA",
    name: "Anjali Chauhan",
    designation: "Fin Acc/Analyst 2",
    company: "Premier Research",
    logo: `${IK}/company-logos/Premier%20Research.png`,
    logoWidth: 140,
    logoHeight: 48,
    image: `${IK}/alumni/Anjali%20Chauhan.png`,
    quote:
      "Getting 400 in both parts of CMA was honestly a surprise for me, but it came from staying consistent with Miles. The placement process was also handled really well, which helped me move into Premier Research.",
    scores: ["400 Part 1", "400 Part 2"],
  },
  {
    id: "cpa-ramandeep-singh",
    track: "CPA",
    name: "Ramandeep Singh",
    designation: "Manager",
    company: "Genpact",
    logo: `${IK}/company-logos/Genpact.png`,
    logoWidth: 110,
    logoHeight: 48,
    image: `${IK}/alumni/Ramandeep%20Singh.png`,
    quote:
      "Miles mentors constantly guided me, the discipline they instilled is what helps me manage teams at Genpact with more confidence.",
    scores: ["85 FAR", "77 AUD", "84 REG", "77 BAR"],
  },
  {
    id: "cpa-zalak-sanghavi",
    track: "CPA",
    name: "Zalak Sanghavi",
    designation: "US Audit Associate",
    company: "BDO",
    logo: `${IK}/company-logos/BDO.png`,
    logoWidth: 124,
    logoHeight: 48,
    image: `${IK}/alumni/Zalak%20Sanghavi.png`,
    quote:
      "Miles mock tests trained me to work under pressure, so real audit deadlines at BDO feel far more manageable than they do for most new hires.",
    scores: ["77 FAR", "85 AUD", "83 REG", "79 BAR"],
  },
  {
    id: "cpa-carolyn-d-sa",
    track: "CPA",
    name: "Carolyn D Sa",
    designation: "Audit Senior",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/CAROLYN%20D%20SA.png`,
    quote:
      "Miles kept me focused and exam-ready, their mentor support helped me move up to Audit Senior at KPMG quicker than most.",
    scores: ["81 FAR", "75 AUD", "80 REG", "75 BAR"],
  },
  {
    id: "cma-abhilash-ashokan-vadassery",
    track: "CMA",
    name: "Abhilash Ashokan Vadassery",
    designation: "Senior Finance Analyst",
    company: "Warner Brothers",
    logo: `${IK}/company-logos/Warner%20brothers.png`,
    logoWidth: 48,
    logoHeight: 48,
    image: `${IK}/alumni/Abhilash%20Ashokan%20Vadassery.png`,
    quote:
      "Miles kept my CMA prep structured, and that same structured thinking is what makes me efficient at Warner Bros today.",
    scores: ["430 Part 1", "410 Part 2"],
  },
  {
    id: "cma-eshan-parashar",
    track: "CMA",
    name: "Eshan Parashar",
    designation: "Fund Accountant",
    company: "SS&C GlobeOp",
    logo: `${IK}/company-logos/SS&C%20GlobeOp.png`,
    logoWidth: 88,
    logoHeight: 48,
    image: `${IK}/alumni/Eshan%20Parashar.png`,
    quote:
      "Miles kept me steady through CMA, and that steadiness is what helps me handle high-pressure reconciliations at SS&C GlobeOp.",
    scores: ["400 Part 1", "400 Part 2"],
  },
  {
    id: "cpa-jaspreet-kaur-dhillon",
    track: "CPA",
    name: "Jaspreet Kaur Dhillon",
    designation: "Assistant Team Lead",
    company: "Zeni AI",
    logo: `${IK}/company-logos/ZENI.png`,
    logoWidth: 189,
    logoHeight: 48,
    image: `${IK}/alumni/Jaspreet%20Kaur%20Dhillon.png`,
    quote:
      "Never feeling stuck during Miles prep taught me to solve problems independently, a habit that fast-tracked me to team lead at Zeni AI.",
    scores: ["87 FAR", "75 AUD", "86 REG", "79 BAR"],
  },
  {
    id: "cma-kavitha-nair",
    track: "CMA",
    name: "Kavitha Nair",
    designation: "Audit Associate",
    company: "Grant Thornton",
    logo: `${IK}/company-logos/GT.png`,
    logoWidth: 258,
    logoHeight: 48,
    image: `${IK}/alumni/Kavitha%20Nair.png`,
    quote:
      "Scoring 440 in both parts was honestly a big moment for me, and Miles played a huge role in that. The transition into placements was just as smooth, and that's how I got into Grant Thornton.",
    scores: ["440 Part 1", "440 Part 2"],
  },
  {
    id: "cpa-vamshi-teja",
    track: "CPA",
    name: "Vamshi Teja",
    designation: "Manager",
    company: "EisnerAmper",
    image: `${IK}/alumni/Vamshi%20Teja.png`,
    quote:
      "Miles mock tests trained me for real exam pressure, and that composure has helped me grow into a manager role at EisnerAmper faster than expected.",
    scores: ["82 FAR", "89 AUD", "77 REG", "78 BAR"],
  },
  {
    id: "cpa-prathamesh-bidkar",
    track: "CPA",
    name: "Prathamesh Bidkar",
    designation: "BI Product Owner",
    company: "Maersk",
    logo: `${IK}/company-logos/Maersk.png`,
    logoWidth: 217,
    logoHeight: 48,
    image: `${IK}/alumni/Prathamesh%20Bidkar.png`,
    quote:
      "Miles flexibility allowed me to balance work and studies without compromising my preparation quality.",
    scores: ["81 FAR", "77 AUD", "87 REG", "80 BAR"],
  },
  {
    id: "cma-simran-saini",
    track: "CMA",
    name: "Simran Saini",
    designation: "Tax Senior Financial Consultant",
    company: "EisnerAmper India",
    image: `${IK}/alumni/Simran%20Saini.png`,
    quote:
      "Miles's systems for consistency is what helped me get into EisnerAmper. Scoring around 420 and 400 just reflected the consistency I built over time along with their placement efforts of course.",
    scores: ["420 Part 1", "400 Part 2"],
  },
  {
    id: "cpa-anusha-lalitha-adibhatla",
    track: "CPA",
    name: "Anusha Lalitha Adibhatla",
    designation: "Analyst",
    company: "Cohnreznick",
    logo: `${IK}/company-logos/Cohnreznick.png`,
    logoWidth: 207,
    logoHeight: 48,
    image: `${IK}/alumni/Anusha%20Lalitha%20Adibhatla.png`,
    quote:
      "I got into Cohnreznick because Miles simplified the toughest topics for me, and that clarity is exactly why I pick up new engagements faster than most.",
    scores: ["77 FAR", "88 AUD", "89 REG", "76 BAR"],
  },
  {
    id: "cpa-bobba-l-p-s-mounika",
    track: "CPA",
    name: "Bobba L P S Mounika",
    designation: "Tax Senior Associate",
    company: "Sentient Solutions",
    logo: `${IK}/company-logos/Sentient%20.png`,
    logoWidth: 208,
    logoHeight: 48,
    image: `${IK}/alumni/Bobba%20L%20P%20S%20Mounika.png`,
    quote:
      "Miles' consistent practice built a work ethic that helps me handle complex tax cases at Sentient Solutions with more ease than expected.",
    scores: ["80 FAR", "77 AUD", "85 REG", "80 BAR"],
  },
  {
    id: "cma-krishnan-p-n-e",
    track: "CMA",
    name: "Krishnan P N E",
    designation: "Associate",
    company: "KPMG",
    logo: `${IK}/company-logos/KPMG.png`,
    logoWidth: 123,
    logoHeight: 48,
    image: `${IK}/alumni/Krishnan%20P%20N%20E.png`,
    quote:
      "Scoring around 430 and 410 felt like I was finally on track with CMA. The consistency I built with Miles across both CMA parts keeps me sharp handling client deliverables as an Associate at KPMG.",
    scores: ["430 Part 1", "410 Part 2"],
  },
  {
    id: "cma-vishal-goel",
    track: "CMA",
    name: "Vishal Goel",
    designation: "Sr. Fund Accountant",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/Vishal%20Goel.png`,
    quote:
      "The placement support of Miles helped me get into Wipro without having any extraordinary scores, the only thing that mattered was my consistency.",
    scores: ["400 Part 1", "410 Part 2"],
  },
  {
    id: "cma-raghav-k-n",
    track: "CMA",
    name: "Raghav K N",
    designation: "Fund Administrator",
    company: "JP Morgan",
    logo: `${IK}/company-logos/JP%20Morgan.png`,
    logoWidth: 236,
    logoHeight: 48,
    image: `${IK}/alumni/Raghav%20K%20N.png`,
    quote:
      "From preparation to placements, everything just felt connected at Miles. And that connected thinking helps me manage fund administration at JP Morgan with more clarity than most.",
    scores: ["400 Part 1", "410 Part 2"],
  },
  {
    id: "cma-venkat-supreeth-s",
    track: "CMA",
    name: "Venkat Supreeth S",
    designation: "Finance Intern",
    company: "HPE",
    logo: `${IK}/company-logos/HPE.png`,
    logoWidth: 115,
    logoHeight: 48,
    image: `${IK}/alumni/Venkat%20Supreeth%20S.png`,
    quote:
      "Scoring 450 in Part 1 was a big boost for me, I didn't expect it honestly. It gave me a problem solving edge that's already visible in my internship at HPE.",
    scores: ["450 Part 1", "400 Part 2"],
  },
  {
    id: "cma-gopika-lakshmi-v",
    track: "CMA",
    name: "Gopika Lakshmi V",
    designation: "Retainer",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/Gopika%20Lakshmi%20V.png`,
    quote:
      "Miles' placement support gave me the opportunity to start my career at Wipro. The consistency and discipline they helped me build throughout both CMA parts continue to keep me reliable and sharp in my role today.",
    scores: ["450 Part 1", "430 Part 2"],
  },
  {
    id: "cpa-vineet-kour",
    track: "CPA",
    name: "Vineet Kour",
    designation: "Junior Accountant",
    company: "Xshore",
    logo: `${IK}/company-logos/Xshore.png`,
    logoWidth: 239,
    logoHeight: 48,
    image: `${IK}/alumni/Vineet%20Kour.png`,
    quote:
      "Scoring 93 in REG was possible because of Miles mocks and revision strategy, I was perfectly exam ready even after being under pressure.",
    scores: ["88 FAR", "86 AUD", "93 REG", "79 BAR"],
  },
  {
    id: "cma-abu-tahir-m",
    track: "CMA",
    name: "Abu Tahir M",
    designation: "SAP Financial Accounting Consultant",
    company: "IBM",
    logo: `${IK}/company-logos/IBM.png`,
    logoWidth: 130,
    logoHeight: 48,
    image: `${IK}/alumni/Abu%20Tahir%20M.png`,
    quote:
      "Even during challenging phases of my preparation, the consistent support and guidance at Miles helped me stay on track. That resilience, along with their placement support, helped me begin my journey at IBM",
    scores: ["440 Part 1", "420 Part 2"],
  },
  {
    id: "cma-arjun-kris-saksena",
    track: "CMA",
    name: "Arjun Kris Saksena",
    designation: "Retainer",
    company: "Wipro",
    logo: `${IK}/company-logos/Wipro.png`,
    logoWidth: 120,
    logoHeight: 48,
    image: `${IK}/alumni/ARJUN%20KRIS%20SAKSENA.png`,
    quote:
      "Scoring 460 in the second part was definitely a highlight for me, Miles supported my overall growth that keeps me competitive at Wipro today.",
    scores: ["430 Part 1", "460 Part 2"],
  },
  {
    id: "cma-abdallah-yasir",
    track: "CMA",
    name: "Abdallah Yasir",
    designation: "Senior Executive",
    company: "PepsiCo",
    logo: `${IK}/company-logos/Pepsico.webp`,
    logoWidth: 166,
    logoHeight: 48,
    image: `${IK}/alumni/Abdallah%20Yasir.png`,
    quote:
      "Placements were something I was a bit unsure about, but Miles handled that really well. I walked into PepsiCo being fully industry ready without any worries!",
    scores: ["400 Part 1", "410 Part 2"],
  },
  {
    id: "cma-s-sankalp",
    track: "CMA",
    name: "S Sankalp",
    designation: "Management Trainee",
    company: "HPE",
    logo: `${IK}/company-logos/HPE.png`,
    logoWidth: 115,
    logoHeight: 48,
    image: `${IK}/alumni/S%20Sankalp.png`,
    quote:
      "Landing my role as a Management Trainee at HPE was possible because of Miles' placement support, but what has helped me succeed in the role is the consistency I built while scoring around 430 in both CMA parts.",
    scores: ["430 Part 1", "430 Part 2"],
  },
  {
    id: "cma-vishnu-vardhan",
    track: "CMA",
    name: "Vishnu Vardhan",
    designation: "KYC Analyst",
    company: "Grant Thornton",
    logo: `${IK}/company-logos/GT.png`,
    logoWidth: 258,
    logoHeight: 48,
    image: `${IK}/alumni/Vishnu%20Vardhan.png`,
    quote:
      "Miles support never made the journey feel solitary, especially during the placements, and that confidence is what helps me handle complex KYC cases at Grant Thornton independently.",
    scores: ["400 Part 1", "410 Part 2"],
  },
];
